import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const Shell = ({ onClose, testid, children }) => (
  <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} onClick={onClose}>
    <motion.div className="modal" data-testid={testid} initial={{ opacity: 0, y: 44, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.45, ease: EASE }} onClick={(e) => e.stopPropagation()}>
      {children}
    </motion.div>
  </motion.div>
);

export function BookingModal({ open, onClose }) {
  const empty = { name: "", contact: "", date: "", time: "", reading_type: "Tarot reading", note: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      onClose();
      setForm(empty);
      toast.success("Request sent — Jyoti will be in touch soon.");
    } catch {
      toast.error("Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="booking-modal">
          <button className="close-button" data-testid="booking-close-button" onClick={onClose} aria-label="Close"><X size={18} /></button>
          <span className="section-kicker">Private Session</span>
          <h2>Book a reading</h2>
          <p className="modal-sub">Tell Jyoti a little about what you’re looking for.</p>
          <form onSubmit={submit}>
            <input required placeholder="Your name" data-testid="booking-name-input" value={form.name} onChange={set("name")} />
            <input required placeholder="Phone or email" data-testid="booking-contact-input" value={form.contact} onChange={set("contact")} />
            <div className="form-row">
              <input required type="date" data-testid="booking-date-input" value={form.date} onChange={set("date")} />
              <select required data-testid="booking-time-select" value={form.time} onChange={set("time")}>
                <option value="">Time</option><option>10:00 am</option><option>12:00 pm</option><option>4:00 pm</option><option>6:00 pm</option>
              </select>
            </div>
            <select data-testid="booking-type-select" value={form.reading_type} onChange={set("reading_type")}>
              <option>Tarot reading</option><option>Birth chart</option><option>Numerology</option>
            </select>
            <textarea placeholder="Anything you’d like to share? (optional)" data-testid="booking-note-input" value={form.note} onChange={set("note")} />
            <button className="gold-button full" data-testid="booking-submit-button" type="submit" disabled={sending}>{sending ? "Sending…" : <>Send request <Send size={15} /></>}</button>
          </form>
        </Shell>
      )}
    </AnimatePresence>
  );
}

export function ReviewModal({ open, onClose, onPublished }) {
  const empty = { name: "", rating: 5, text: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      onPublished(data);
      onClose();
      setForm(empty);
      toast.success("Thank you for sharing your experience.");
    } catch {
      toast.error("Could not publish your review.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Shell onClose={onClose} testid="review-modal">
          <button className="close-button" data-testid="review-close-button" onClick={onClose} aria-label="Close"><X size={18} /></button>
          <span className="section-kicker">Your Experience</span>
          <h2>Write a review</h2>
          <form onSubmit={submit}>
            <input required placeholder="Your name" data-testid="review-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="rating-picker" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} className={n <= form.rating ? "selected" : ""} data-testid={`review-star-${n}`} onClick={() => setForm({ ...form, rating: n })}>★</button>
              ))}
            </div>
            <textarea required placeholder="Share your experience" data-testid="review-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
            <button className="gold-button full" data-testid="review-submit-button" type="submit" disabled={sending}>{sending ? "Publishing…" : "Publish review"}</button>
          </form>
        </Shell>
      )}
    </AnimatePresence>
  );
}
